/**
 * AI Chat panel for conversing about a specific NVC form.
 * Displays chat history and allows sending new messages.
 */
import { useEffect, useRef, useState } from 'react';
import { ChatMessage } from '../../types/chat';
import { sendChatMessage, clearChatHistory } from '../../services/forms';

interface AiChatPanelProps {
  formId: string;
  initialHistory: ChatMessage[];
}

export default function AiChatPanel({ formId, initialHistory }: AiChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>(initialHistory);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  async function handleSend() {
    const text = inputValue.trim();
    if (!text || isLoading) return;

    setInputValue('');
    setError(null);
    setIsLoading(true);

    // Optimistically add user message
    const userMsg: ChatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);

    try {
      const result = await sendChatMessage(formId, text);
      setMessages(result.chat_history);
    } catch (err) {
      setError('Nie udało się wysłać wiadomości. Spróbuj ponownie.');
      // Remove optimistic user message on error
      setMessages(prev => prev.filter(m => m !== userMsg));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleClearConfirmed() {
    setShowClearConfirm(false);
    setError(null);
    try {
      await clearChatHistory(formId);
      setMessages([]);
    } catch {
      setError('Nie udało się wyczyścić historii czatu.');
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div
      aria-label="Panel czatu AI"
      style={{
        marginTop: '20px',
        border: '1px solid #ce93d8',
        borderRadius: '8px',
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      {/* Header */}
      <div style={{
        background: '#9c27b0',
        color: 'white',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span style={{ fontWeight: '600', fontSize: '15px' }}>💬 Czat z AI</span>
        {messages.length > 0 && (
          <button
            onClick={() => setShowClearConfirm(true)}
            disabled={isLoading}
            aria-label="Wyczyść historię czatu"
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.5)',
              color: 'white',
              borderRadius: '4px',
              padding: '4px 10px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              opacity: isLoading ? 0.5 : 1,
            }}
          >
            Wyczyść czat
          </button>
        )}
      </div>

      {/* Confirmation dialog for clear */}
      {showClearConfirm && (
        <div style={{
          background: '#fff3e0',
          padding: '12px 16px',
          borderBottom: '1px solid #ffe0b2',
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
        }}>
          <span style={{ fontSize: '14px', flex: 1 }}>
            Czy na pewno chcesz wyczyścić historię czatu? Tej operacji nie można cofnąć.
          </span>
          <button
            onClick={handleClearConfirmed}
            aria-label="Potwierdź wyczyszczenie czatu"
            style={{
              background: '#d32f2f',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 14px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Wyczyść
          </button>
          <button
            onClick={() => setShowClearConfirm(false)}
            aria-label="Anuluj wyczyszczenie czatu"
            style={{
              background: '#e0e0e0',
              color: '#333',
              border: 'none',
              borderRadius: '4px',
              padding: '6px 14px',
              cursor: 'pointer',
              fontSize: '13px',
            }}
          >
            Anuluj
          </button>
        </div>
      )}

      {/* Message list */}
      <div
        role="log"
        aria-live="polite"
        aria-label="Historia wiadomości czatu"
        style={{
          minHeight: '120px',
          maxHeight: '350px',
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        {messages.length === 0 && !isLoading && (
          <p style={{ color: '#999', fontSize: '14px', textAlign: 'center', margin: 'auto' }}>
            Zadaj pytanie dotyczące formularza lub feedbacku empAI.
          </p>
        )}

        {messages.map((msg, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div style={{
              maxWidth: '80%',
              padding: '10px 14px',
              borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
              background: msg.role === 'user' ? '#9c27b0' : '#f3e5f5',
              color: msg.role === 'user' ? 'white' : '#333',
              fontSize: '14px',
              lineHeight: '1.5',
              whiteSpace: 'pre-wrap',
            }}>
              {msg.content}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{
              padding: '10px 14px',
              borderRadius: '12px 12px 12px 2px',
              background: '#f3e5f5',
              color: '#9c27b0',
              fontSize: '14px',
            }}
              aria-label="AI pisze odpowiedź"
            >
              AI pisze...
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          padding: '8px 16px',
          background: '#ffebee',
          color: '#c62828',
          fontSize: '13px',
          borderTop: '1px solid #ffcdd2',
        }}>
          {error}
        </div>
      )}

      {/* Input area */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-end',
        background: '#fafafa',
      }}>
        <textarea
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          placeholder="Wpisz pytanie do AI... (Enter = wyślij, Shift+Enter = nowa linia)"
          aria-label="Wpisz pytanie do AI"
          rows={2}
          style={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid #ce93d8',
            borderRadius: '6px',
            fontSize: '14px',
            resize: 'none',
            outline: 'none',
            fontFamily: 'inherit',
            opacity: isLoading ? 0.6 : 1,
            cursor: isLoading ? 'not-allowed' : 'text',
          }}
        />
        <button
          onClick={handleSend}
          disabled={isLoading || !inputValue.trim()}
          aria-label="Wyślij wiadomość do AI"
          style={{
            padding: '10px 18px',
            background: '#9c27b0',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (isLoading || !inputValue.trim()) ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            opacity: (isLoading || !inputValue.trim()) ? 0.5 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          Wyślij
        </button>
      </div>
    </div>
  );
}
